import { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import useTranslation from '../data/useTranslation';

const SITE_URL = 'https://beyondpersonalmatrix.com';
const OG_IMAGE = `${SITE_URL}/bpm-logo.png`;

const HREFLANG_ID = 'bpm-hreflang';
const JSONLD_ID = 'bpm-jsonld';

function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function setHreflang() {
  // Remove previous hreflang links
  document.head.querySelectorAll(`link[data-bpm="${HREFLANG_ID}"]`).forEach((n) => n.remove());
  const langs = [
    { hreflang: 'fr', href: `${SITE_URL}/?lang=fr` },
    { hreflang: 'en', href: `${SITE_URL}/?lang=en` },
    { hreflang: 'x-default', href: `${SITE_URL}/?lang=en` },
  ];
  langs.forEach(({ hreflang, href }) => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hreflang);
    link.setAttribute('href', href);
    link.setAttribute('data-bpm', HREFLANG_ID);
    document.head.appendChild(link);
  });
}

function setJsonLd(language) {
  const existing = document.getElementById(JSONLD_ID);
  if (existing) existing.remove();

  const isFR = language === 'fr';
  const description = isFR
    ? 'Beyond Personal Matrix, Tests psychométriques validés + analyse IA personnalisée pour révéler votre potentiel.'
    : 'Beyond Personal Matrix, Validated psychometric tests + personalized AI analysis to reveal your potential.';

  const products = [
    {
      '@type': 'Product',
      name: isFR ? 'Découverte' : 'Discovery',
      description: isFR ? '2 tests psychométriques + rapport IA simplifié' : '2 psychometric tests + simplified AI report',
      offers: { '@type': 'Offer', price: '9.99', priceCurrency: 'EUR', availability: 'https://schema.org/InStock' },
    },
    {
      '@type': 'Product',
      name: isFR ? 'Complète' : 'Complete',
      description: isFR ? '6 tests psychométriques + rapport IA complet + plan de développement' : '6 psychometric tests + full AI report + development plan',
      offers: { '@type': 'Offer', price: '29.99', priceCurrency: 'EUR', availability: 'https://schema.org/InStock' },
    },
    {
      '@type': 'Product',
      name: 'Duo',
      description: isFR ? 'Pack 2 personnes + rapport de compatibilité IA' : '2-person bundle + AI compatibility report',
      offers: { '@type': 'Offer', price: '39.99', priceCurrency: 'EUR', availability: 'https://schema.org/InStock' },
    },
  ];

  const ld = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: 'Beyond Personal Matrix',
        url: SITE_URL,
        logo: OG_IMAGE,
        sameAs: [],
      },
      ...products,
    ],
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = JSONLD_ID;
  script.text = JSON.stringify(ld);
  document.head.appendChild(script);
}

function SEOHead() {
  const { language } = useLanguage();
  const { t } = useTranslation();

  useEffect(() => {
    const isFR = language === 'fr';
    const title = isFR
      ? 'BPM, Beyond Personal Matrix | Tests psychométriques + IA'
      : 'BPM, Beyond Personal Matrix | Psychometric tests + AI';
    const description = isFR
      ? 'Découvrez votre potentiel avec 6 tests psychométriques validés scientifiquement et une analyse IA personnalisée. Dès 9,99€.'
      : 'Discover your potential with 6 scientifically validated psychometric tests and a personalized AI analysis. From €9.99.';
    const ogLocale = isFR ? 'fr_FR' : 'en_US';

    document.documentElement.lang = language;
    document.title = title;
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:locale', ogLocale);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', SITE_URL);
    setMeta('property', 'og:image', OG_IMAGE);
    setMeta('property', 'og:site_name', 'Beyond Personal Matrix');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', OG_IMAGE);

    setHreflang();
    setJsonLd(language);
  }, [language, t]);

  return null;
}

export default SEOHead;
